// src/api/auth.ts
import axios from "axios";
import {
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  UpdateProfileRequest,
  UserProfileResponse,
  UsersResponse,
} from "../interfaces/IUser";
import {
  IFavoriteProduct,
  Product,
  ProductResponse,
  ProductsQueryParams,
  PublicProduct,
  PublicProductDetails,
  PublicProductListResponse,
  UpdateProductRequest,
  UpdateProductResponse,
} from "interfaces/IProduct";
import { CategoriesResponse, Category } from "interfaces/ICategory";
import { ICreatedSize, Size, SizesResponse } from "interfaces/ISize";
import {
  ProductCreateRequest,
  ProductCreateResponse,
} from "../interfaces/IProduct";
import { HttpResponse } from "./HttpResponse";
import { catchErrorOnRequest } from "./requests";
import {
  CreatePromotionRequest,
  CreatePromotionResponse,
  Promotion,
  PromotionDetails,
  PublicPromotion,
  UpdatePromotionRequest,
  UpdatePromotionResponse,
} from "interfaces/IPromotion";
import { CategoryProductsResponse } from "interfaces/ICategoryProduct";
import {
  ProductPromotionsRequest,
  UpdateProductPromotionsRequest,
  UpdateProductPromotionsResponse,
} from "interfaces/IProductPromotion";
import { IOrderDetails, IOrdersResponse } from "interfaces/IOrder";
import { IUpdateOrder } from "interfaces/IUpdateOrder";
import { Address, AddressResponse } from "interfaces/IAddresses";
import { IBankAccount, IPostBankAccount } from "interfaces/IBankAccount";
import { IPaymentMethod } from "interfaces/IPaymentMethod";
import { PaymentMethodsResponse } from "interfaces/IPaymentMethod";
import {
  IClientOrderFilters,
  IPaginatedClientOrders,
} from "interfaces/IClientOrders";
import { ICreateOrderRequest, ICreateOrderResponse } from "interfaces/IOrder";
import {
  IShippingCost,
  IUpdateShippingCostRequest,
  IUpdateShippingCostResponse,
} from "interfaces/IShippingCost";
import { IContactRequest, IContactResponse } from "interfaces/IContact";
import { IReviewRequest, IReviewResponse, IReviewDetailResponse } from "../interfaces/IReviews";
import {
  ISalesReportResponse,
  IBestSellingProductsResponse,
  INewUsersReportResponse,
  IFrequentUsersResponse,
  IDashboardSummaryResponse,
  ReportPeriod,
  DashboardPeriod
} from "../interfaces/IReports";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";

export const createAuthenticatedAxiosInstance = (token: string | null) => {
  const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return axiosInstance;
};
/**
 * Login function
 * @param email - User's email
 * @param password - User's password
 * @returns Response data from the API
 * @throws Error if the request fails
 */
export const login = async (email: string, password: string) => {
  try {
    const response = await axios.post(`${API_URL}/login`, {
      email,
      password,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Login function
 * @param email - Client's email
 * @param password - Client's password
 * @returns Response data from the API
 * @throws Error if the request fails
 */
export const clientLogin = async (email: string, password: string) => {
  try {
    const response = await axios.post(`${API_URL}/client-login`, {
      email,
      password,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Login with Google function
 * @param email - User's email from Google
 * @param name - User's name from Google
 * @param credential - Google credential token
 * @returns Response data from the API
 * @throws Error if the request fails
 */
export const loginWithGoogle = async (
  email: string,
  name: string,
  credential: string
) => {
  // Validar parámetros de entrada
  if (!email || !name || !credential) {
    throw new Error(
      "Faltan datos requeridos para el inicio de sesión con Google"
    );
  }

  if (!email.includes("@")) {
    throw new Error("Email inválido recibido de Google");
  }

  try {
    const response = await axios.post(`${API_URL}/login-with-google`, {
      email,
      name,
      credential,
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Detalles del error de Axios:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
      });

      // Crear mensajes de error más específicos
      if (error.response?.status === 500) {
        const serverMessage =
          error.response?.data?.message || "Error interno del servidor";
        throw new Error(`Error del servidor (500): ${serverMessage}`);
      } else if (error.response?.status === 400) {
        const validationMessage =
          error.response?.data?.message || "Datos de Google inválidos";
        throw new Error(`Error de validación (400): ${validationMessage}`);
      } else if (error.response?.status === 401) {
        throw new Error("Credenciales de Google inválidas o expiradas (401)");
      } else if (error.response?.status === 403) {
        throw new Error(
          "No tienes permisos para acceder con esta cuenta de Google (403)"
        );
      } else if (
        error.code === "ECONNREFUSED" ||
        error.code === "ERR_NETWORK"
      ) {
        throw new Error(
          "No se puede conectar con el servidor. Verifica tu conexión."
        );
      }
    }

    // Si no es un error de Axios, lanzar el error original
    throw error;
  }
};

/**
 * Initiates the password recovery process for a given email.
 * @param email - The email address of the user requesting password recovery.
 * @returns The response data from the API.
 * @throws Any error that occurs during the API call.
 */
export const recoverPassword = async (email: string) => {
  try {
    const response = await axios.post(`${API_URL}/password/recover`, { email });
    return { data: response.data, status: response.status };
  } catch (error) {
    throw error;
  }
};

/**
 * Validates the verification code sent to the user's email.
 * @param email - The email address of the user.
 * @param verification_code - The verification code to be validated.
 * @returns The response data from the API.
 * @throws Any error that occurs during the API call.
 */
export const validateVerificationCode = async (
  email: string,
  verification_code: string
) => {
  try {
    const response = await axios.post(
      `${API_URL}/password/validate-verification-code`,
      { email, verification_code }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Changes the user's password after successful verification.
 * @param password - The new password to be set.
 * @param email - The email address of the user.
 * @param verification_code - The verification code for authentication.
 * @returns The response data from the API.
 * @throws Any error that occurs during the API call.
 */
export const changePassword = async (
  password: string,
  email: string,
  verification_code: string
) => {
  try {
    const response = await axios.post(`${API_URL}/password/change`, {
      password,
      email,
      verification_code,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};
/**
 * Fetches users from the API with optional filtering and pagination
 * @param {Object} params - The parameters for filtering and pagination
 * @param {number} [params.page] - The page number to fetch
 * @param {number} [params.per_page] - The number of items per page
 * @param {string} [params.name] - Filter users by name
 * @param {string} [params.email] - Filter users by email
 * @param {string} [params.role] - Filter users by role
 * @param {string} [params.created_from] - Filter users created from this date
 * @param {string} [params.created_to] - Filter users created up to this date
 * @param {string} [params.sort_by] - The field to sort the results by
 * @param {'asc' | 'desc'} [params.order] - The order of sorting (ascending or descending)
 * @returns {Promise<UsersResponse>} A promise that resolves to the UsersResponse object
 * @throws {Error} If there's an error fetching the users
 */
export const fetchUsers = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  params: {
    page?: number;
    per_page?: number;
    name?: string;
    email?: string;
    role?: string;
    created_from?: string;
    created_to?: string;
    sort_by?: string;
    order?: "asc" | "desc";
  }
) => {
  try {
    const response = await axiosInstance.get<UsersResponse>("/users", {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches user details by ID from the API
 * @param axiosInstance - The authenticated axios instance
 * @param userId - The ID of the user to fetch
 * @returns The user data from the API response
 * @throws Any error that occurs during the API call
 */
export const fetchUserById = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  userId: number
) => {
  try {
    const response = await axiosInstance.get(`/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Updates a user's information
 * @param axiosInstance - The authenticated axios instance
 * @param userId - The ID of the user to update
 * @param userData - The data to update (can be partial)
 * @returns The updated user data from the API response
 * @throws Any error that occurs during the API call
 */
export const updateUser = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  userId: number,
  userData: Partial<{
    name: string;
    email: string;
    password: string;
    role: string;
    status: boolean;
  }>
) => {
  try {
    const response = await axiosInstance.put(`/users/${userId}`, userData);
    return { data: response.data, status: response.status };
  } catch (error) {
    throw error;
  }
};

/**
 * Creates a new user in the system
 * @async
 * @function createUser
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated Axios instance
 * @param {Object} userData - User data for creation
 * @param {string} userData.name - User's name
 * @param {string} userData.email - User's email address
 * @param {string} userData.password - User's password
 * @param {string} userData.role - User's role in the system
 * @returns {Promise<{data: any, status: number}>} Object containing response data and status code
 * @throws {Error} Throws an error if the API call fails
 */
export const createUser = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  userData: {
    name: string;
    email: string;
    password: string;
    role: string;
  }
) => {
  try {
    const response = await axiosInstance.post("/register", userData);
    return { data: response.data, status: response.status };
  } catch (error) {
    throw error;
  }
};

/**
 * Registers a new client with the provided name, email, and password.
 * @param name - The name of the client.
 * @param email - The email address of the client.
 * @param password - The password for the client's account.
 * @returns A promise that resolves to the API response data, including the cacheKey.
 * @throws Will throw an error if the API request fails.
 */
export const registerClient = async (
  name: string,
  email: string,
  password: string
) => {
  try {
    const response = await axios.post(`${API_URL}/register-client`, {
      name,
      email,
      password,
    });
    const { success, message, cacheKey } = response.data;
    return { success, message, cacheKey };
  } catch (error) {
    throw error;
  }
};

/**
 * Verifies a client's account using the provided verification code and cacheKey.
 * @param code - The verification code sent to the client's email.
 * @param cacheKey - The cacheKey received from the registerClient response.
 * @returns A promise that resolves to the API response data.
 * @throws Will throw an error if the API request fails.
 */
export const verifyClient = async (code: string, cacheKey: string) => {
  try {
    const response = await axios.post(`${API_URL}/verify-client`, {
      code,
      cacheKey,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches products from the API with optional filtering and pagination
 * @param {Object} axiosInstance - The authenticated axios instance
 * @param {Object} params - The parameters for filtering and pagination
 * @param {number} [params.page] - The page number to fetch
 * @param {number} [params.per_page] - The number of items per page
 * @param {string} [params.name] - Filter products by name
 * @param {string} [params.category] - Filter products by category
 * @param {string} [params.brand] - Filter products by brand
 * @param {string} [params.created_from] - Filter products created from this date
 * @param {string} [params.created_to] - Filter products created up to this date
 * @param {string} [params.sort_by] - The field to sort the results by
 * @param {'asc' | 'desc'} [params.order] - The order of sorting (ascending or descending)
 * @returns {Promise<ProductResponse>} A promise that resolves to the ProductResponse object
 * @throws {Error} If there's an error fetching the products
 */
export const fetchProducts = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  params: {
    page?: number;
    per_page?: number;
    name?: string;
    category?: string;
    status?: string;
    brand?: string;
    created_from?: string;
    created_to?: string;
    sort_by?: string;
    order?: "asc" | "desc";
  }
) => {
  try {
    const response = await axiosInstance.get<ProductResponse>("/products", {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches categories from the API with optional filtering and pagination
 * @async
 * @function fetchCategories
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated axios instance
 * @param {Object} params - The parameters for filtering and pagination
 * @param {string} [params.name] - Filter categories by name
 * @param {string} [params.status] - Filter categories by status
 * @param {string} [params.sort_by] - The field to sort the results by
 * @param {'asc' | 'desc'} [params.order] - The order of sorting (ascending or descending)
 * @param {number} [params.page=1] - The page number to fetch
 * @param {number} [params.per_page=10] - The number of items per page
 * @returns {Promise<CategoriesResponse>} A promise that resolves to the CategoriesResponse object
 * @throws {Error} If there's an error fetching the categories
 */
export const fetchCategories = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  params: {
    name?: string;
    status?: string;
    sort_by?: string;
    order?: "asc" | "desc";
    page?: number;
    per_page?: number;
  }
) => {
  try {
    const response = await axiosInstance.get<CategoriesResponse>(
      "/categories",
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches sizes from the API with optional filtering and pagination
 * @async
 * @function fetchSizes
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated axios instance
 * @param {Object} params - The parameters for filtering and pagination
 * @param {string} [params.name] - Filter sizes by name
 * @param {string} [params.description] - Filter sizes by description
 * @param {number} [params.status] - Filter sizes by status
 * @param {string} [params.sort_by] - The field to sort the results by
 * @param {'asc' | 'desc'} [params.order] - The order of sorting (ascending or descending)
 * @param {number} [params.page=1] - The page number to fetch
 * @param {number} [params.per_page=10] - The number of items per page
 * @returns {Promise<SizesResponse>} A promise that resolves to the SizesResponse object
 * @throws {Error} If there's an error fetching the sizes
 */
export const fetchSizes = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  params: {
    name?: string;
    description?: string;
    status?: number;
    sort_by?: string;
    order?: "asc" | "desc";
    page?: number;
    per_page?: number;
  }
): Promise<HttpResponse<SizesResponse>> => {
  try {
    const { data } = await axiosInstance.get<SizesResponse>("/sizes", {
      params: {
        page: params.page || 1,
        per_page: params.per_page || 10,
        ...params,
      },
    });
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<SizesResponse>(error);
  }
};

/**
 * api call that attempts to create a new size
 */
export const postSize = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  newSize: { name: string; description: string }
): Promise<HttpResponse<ICreatedSize>> => {
  const { name, description } = newSize;
  try {
    const { data } = await axiosInstance.post<ICreatedSize>("/sizes", {
      name,
      description,
    });
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<ICreatedSize>(error);
  }
};

/**
 * Api call that ask for a size details
 * @param axiosInstance an instance of the user session's token
 * @param sizeId the id of the size
 */
export const showSize = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  sizeId: number
): Promise<HttpResponse<Size>> => {
  try {
    const { data } = await axiosInstance.get<Size>(`/sizes/${sizeId}`);
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<Size>(error);
  }
};

/**
 * Api call that patches the size. It patch the status property of the required size
 * @param axiosInstance an instance of the user session's token
 * @param sizeId The id of the required size
 * @param sizeState True if the user wants to deactivate false if deactivate
 */
export const patchSize = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  sizeId: number,
  sizeState: boolean
): Promise<{ data: any; statusCode: number }> => {
  try {
    if (sizeState) {
      const response = await axiosInstance.patch<{ message: string }>(
        `/sizes/${sizeId}/deactivate`
      );
      return { data: response.data, statusCode: response.status };
    } else {
      const response = await axiosInstance.patch<{ message: string }>(
        `/sizes/${sizeId}/activate`
      );
      return { data: response.data, statusCode: response.status };
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { data: error.response.data, statusCode: error.response.status };
    }
    throw error;
  }
};

/**
 * Api call that updates the size. It patch the name and description properties of the required size
 * @param axiosInstance an instance of the user session's token
 * @param sizeId The id of the required size
 * @param update is the update object of the name and/or description
 */
export const updateSize = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  sizeId: number,
  update: { name: string; description: string }
): Promise<HttpResponse<{ message: string }>> => {
  const { name, description } = update;
  try {
    const { data } = await axiosInstance.patch<{ message: string }>(
      `/sizes/${sizeId}`,
      { name, description }
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<{ message: string }>(error);
  }
};

export const createProduct = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  productData: ProductCreateRequest
): Promise<{ data: ProductCreateResponse; status: number }> => {
  try {
    const response = await axiosInstance.post<ProductCreateResponse>(
      "/products",
      productData
    );
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { data: error.response.data, status: error.response.status };
    }
    throw error;
  }
};
/**
 * Fetches a product by its ID from the API.
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated Axios instance.
 * @param {number} productId - The ID of the product to fetch.
 * @returns {Promise<any>} A promise that resolves to the product data.
 * @throws {Error} Throws any error that occurs during the API call.
 */
export const fetchProductById = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  productId: number
): Promise<any> => {
  try {
    const response = await axiosInstance.get<{ data: Product }>(
      `/products/${productId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Activates a product by its ID.
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated Axios instance.
 * @param {number} productId - The ID of the product to activate.
 * @returns {Promise<{ message: string }>} A promise that resolves to an object containing a success message.
 * @throws {Error} Throws an error if the product is not found or if there's a server error.
 */
export const activateProduct = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  productId: number
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.patch<{ message: string }>(
      `/products/${productId}/activate`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error("Producto no encontrado.");
      } else if (error.response?.status === 500) {
        throw new Error("Error interno del servidor.");
      }
    }
    throw error;
  }
};

/**
 * Deactivates a product by its ID.
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated Axios instance.
 * @param {number} productId - The ID of the product to deactivate.
 * @returns {Promise<{ message: string }>} A promise that resolves to an object containing a success message.
 * @throws {Error} Throws an error if the product is not found or if there's a server error.
 */
export const deactivateProduct = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  productId: number
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.patch<{ message: string }>(
      `/products/${productId}/deactivate`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error("Producto no encontrado.");
      } else if (error.response?.status === 500) {
        throw new Error("Error interno del servidor.");
      }
    }
    throw error;
  }
};

/**
 * Updates a product with the given ID using the provided data.
 *
 * @param axiosInstance - An authenticated Axios instance for making API requests.
 * @param productId - The ID of the product to be updated.
 * @param updateData - An object containing the fields to be updated on the product.
 * @returns A promise that resolves to the updated product data.
 */
export const updateProduct = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  productId: number,
  updateData: UpdateProductRequest
): Promise<UpdateProductResponse> => {
  try {
    const response = await axiosInstance.patch<UpdateProductResponse>(
      `/products/${productId}`,
      updateData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating product:", error);
    throw error;
  }
};

/**
 * Creates a new category
 * @function createCategory
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {Object} categoryData - Category data to create
 * @param {string} categoryData.name - Name of the category
 * @param {string[]} categoryData.images - Array of base64 encoded images
 * @returns {Promise<{data: any, status: number}>} Response containing created category data and status
 * @throws {Error} When the API request fails
 */
export const createCategory = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  categoryData: {
    name: string;
    images: string[];
  }
): Promise<{ data: any; status: number }> => {
  try {
    const response = await axiosInstance.post("/categories", categoryData);
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { data: error.response.data, status: error.response.status };
    }
    throw error;
  }
};

/**
 * Fetches a category by its ID
 * @function fetchCategoryById
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {number} categoryId - ID of the category to fetch
 * @returns {Promise<Category>} Promise that resolves to the category data
 * @throws {Error} When the API request fails
 */
export const fetchCategoryById = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  categoryId: number
): Promise<any> => {
  try {
    const response = await axiosInstance.get<{ data: Category }>(
      `/categories/${categoryId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Activates a category by its ID
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated Axios instance
 * @param {number} categoryId - The ID of the category to activate
 * @returns {Promise<{ message: string }>} A promise that resolves to a success message
 */
export const activateCategory = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  categoryId: number
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.patch<{ message: string }>(
      `/categories/${categoryId}/activate`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Deactivates a category by its ID
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated Axios instance
 * @param {number} categoryId - The ID of the category to deactivate
 * @returns {Promise<{ message: string }>} A promise that resolves to a success message
 */
export const deactivateCategory = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  categoryId: number
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.patch<{ message: string }>(
      `/categories/${categoryId}/deactivate`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Updates a category with the given ID using the provided data.
 * @param axiosInstance - An authenticated Axios instance for making API requests.
 * @param categoryId - The ID of the category to be updated.
 * @param updateData - An object containing the fields to be updated on the category.
 * @returns A promise that resolves to the updated category data.
 */
export const updateCategory = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  categoryId: number,
  updateData: {
    name: string;
    status?: number;
    images: string[];
    deleted_images: string[];
  }
): Promise<any> => {
  try {
    const response = await axiosInstance.patch(
      `/categories/${categoryId}`,
      updateData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches promotions from the API with optional filtering and pagination
 * @async
 * @function fetchPromotions
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated axios instance
 * @param {Object} params - The parameters for filtering and pagination
 * @param {string} [params.name] - Filter promotions by name
 * @param {string} [params.status] - Filter promotions by status
 * @param {string} [params.sort_by] - The field to sort the results by
 * @param {'asc' | 'desc'} [params.order] - The order of sorting (ascending or descending)
 * @param {number} [params.page=1] - The page number to fetch
 * @param {number} [params.per_page=10] - The number of items per page
 * @returns {Promise<{current_page: number, total_pages: number, total_items: number, promotions: Promotion[]}>} A promise that resolves to the promotions response object
 * @throws {Error} If there's an error fetching the promotions
 */
export const fetchPromotions = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  params: {
    name?: string;
    status?: string;
    sort_by?: string;
    order?: "asc" | "desc";
    page?: number;
    per_page?: number;
  }
) => {
  try {
    const response = await axiosInstance.get<{
      current_page: number;
      total_pages: number;
      total_items: number;
      promotions: Promotion[];
    }>("/promotions", {
      params: {
        page: params.page || 1,
        per_page: params.per_page || 10,
        ...params,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Creates a new promotion in the system
 * @async
 * @function createPromotion
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {CreatePromotionRequest} promotionData - Promotion data to create
 * @param {string} promotionData.name - Name of the promotion
 * @param {'$' | '%'} promotionData.type - Type of discount (amount or percentage)
 * @param {string} promotionData.description - Description of the promotion
 * @param {string} promotionData.start_date - Start date in YYYY-MM-DD format
 * @param {string} promotionData.end_date - End date in YYYY-MM-DD format
 * @param {number} promotionData.discount - Discount value
 * @param {string[]} promotionData.images - Array of base64 encoded images
 * @returns {Promise<{data: CreatePromotionResponse, status: number}>} Object containing created promotion data and status
 * @throws {Error} When the API request fails
 */
export const createPromotion = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  promotionData: CreatePromotionRequest
): Promise<{ data: CreatePromotionResponse; status: number }> => {
  try {
    const response = await axiosInstance.post<CreatePromotionResponse>(
      "/promotions",
      promotionData
    );
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { data: error.response.data, status: error.response.status };
    }
    throw error;
  }
};

/**
 * Fetches product IDs associated with given category IDs
 * @async
 * @function fetchCategoryProducts
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {number[]} categoryIds - Array of category IDs to filter products
 * @returns {Promise<CategoryProductsResponse>} Promise that resolves to product IDs associated with the categories
 * @throws {Error} When the API request fails
 */
export const fetchCategoryProducts = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  categoryIds: number[]
): Promise<CategoryProductsResponse> => {
  try {
    const response = await axiosInstance.post<CategoryProductsResponse>(
      "/categories/products/filter",
      { category_ids: categoryIds }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Creates associations between products and promotions
 * @async
 * @function createProductPromotions
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {ProductPromotionsRequest} data - Request data containing product and promotion IDs
 * @param {number[]} data.product_ids - Array of product IDs
 * @param {number[]} data.promotion_ids - Array of promotion IDs
 * @returns {Promise<{data: any, status: number}>} Object containing response data and status
 * @throws {Error} When the API request fails
 */
export const createProductPromotions = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  data: ProductPromotionsRequest
): Promise<{ data: any; status: number }> => {
  try {
    const response = await axiosInstance.post("/product-promotions", data);
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { data: error.response.data, status: error.response.status };
    }
    throw error;
  }
};

/**
 * Fetches a promotion by its ID
 * @async
 * @function fetchPromotionById
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {number} promotionId - ID of the promotion to fetch
 * @returns {Promise<PromotionDetailsResponse>} Promise that resolves to the promotion details
 * @throws {Error} When the API request fails
 */
export const fetchPromotionById = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  promotionId: number
): Promise<PromotionDetails> => {
  try {
    const response = await axiosInstance.get<PromotionDetails>(
      `/promotions/${promotionId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Updates an existing promotion
 * @function updatePromotion
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {number} promotionId - ID of the promotion to update
 * @param {UpdatePromotionRequest} promotionData - New promotion data
 * @returns {Promise<UpdatePromotionResponse>} Updated promotion information
 * @throws {Error} When API request fails
 */
export const updatePromotion = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  promotionId: number,
  promotionData: UpdatePromotionRequest
): Promise<UpdatePromotionResponse> => {
  try {
    const response = await axiosInstance.patch<UpdatePromotionResponse>(
      `/promotions/${promotionId}`,
      promotionData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Updates product associations for a promotion
 * @function updateProductPromotions
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {number} promotionId - ID of the promotion
 * @param {UpdateProductPromotionsRequest} data - Product IDs to associate
 * @returns {Promise<UpdateProductPromotionsResponse>} Confirmation message
 * @throws {Error} When API request fails
 */
export const updateProductPromotions = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  promotionId: number,
  data: UpdateProductPromotionsRequest
): Promise<UpdateProductPromotionsResponse> => {
  try {
    const response = await axiosInstance.put<UpdateProductPromotionsResponse>(
      `/product-promotions/${promotionId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Activates a promotion
 * @function activatePromotion
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {number} promotionId - ID of the promotion to activate
 * @returns {Promise<{message: string}>} Confirmation message
 * @throws {Error} When API request fails
 */
export const activatePromotion = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  promotionId: number
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.patch<{ message: string }>(
      `/promotions/${promotionId}/activate`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Deactivates a promotion
 * @function deactivatePromotion
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Authenticated axios instance
 * @param {number} promotionId - ID of the promotion to deactivate
 * @returns {Promise<{message: string}>} Confirmation message
 * @throws {Error} When API request fails
 */
export const deactivatePromotion = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  promotionId: number
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.patch<{ message: string }>(
      `/promotions/${promotionId}/deactivate`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches public categories from the API
 * @function fetchPublicCategories
 * @returns {Promise<Category[]>} Array of public categories with their images
 * @throws {Error} When API request fails
 */
export const fetchPublicCategories = async (): Promise<Category[]> => {
  try {
    const response = await axios.get<Category[]>(
      `${API_URL}/public/categories`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches public promotions from the API
 * @returns Promise containing array of PublicPromotion objects
 * @throws Error if API request fails
 */
export const fetchPublicPromotions = async (): Promise<PublicPromotion[]> => {
  try {
    const response = await axios.get<PublicPromotion[]>(
      `${API_URL}/public/promotions`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches newest products from the API
 * @returns Promise containing array of PublicProduct objects
 * @throws Error if API request fails
 */
export const fetchNewestProducts = async (): Promise<PublicProduct[]> => {
  try {
    const response = await axios.get<PublicProduct[]>(
      `${API_URL}/public/newest-products`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/*
 * Fetches sizes from the API with optional filtering and pagination
 * @async
 * @function fetchSizes
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated axios instance
 * @param {Object} params - The parameters for filtering and pagination
 * @param {string} [params.user_id] - The user id
 * @param {string} [params.order_date_from] - Filter the orders by date when it was created
 * @param {string} [params.order_date_to] - Filter the orders by date when it's going to be delivered
 * @param {string} [params.payment_method] - Filter the orders by payment method
 * @param {string} [params.status] - Filter the orders by status
 * @param {string} [params.sort_by] - The field to sort the results by
 * @param {'asc' | 'desc'} [params.order] - The order of sorting (ascending or descending)
 * @param {number} [params.page=1] - The page number to fetch
 * @param {number} [params.per_page=10] - The number of items per page
 * @returns {Promise<IOrdersResponse>} A promise that resolves to the SizesResponse object
 * @throws {Error} If there's an error fetching the sizes
 */
export const fetchOrders = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  params: {
    user_id?: number;
    order_date_from?: string;
    order_date_to?: string;
    payment_method?: string;
    status?: string;
    sort_by?: string;
    order?: "asc" | "desc";
    page?: number;
    per_page?: number;
  }
): Promise<HttpResponse<IOrdersResponse>> => {
  try {
    const { data } = await axiosInstance.get<IOrdersResponse>("/orders", {
      params: {
        page: params.page || 1,
        per_page: params.per_page || 10,
        ...params,
      },
    });
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<IOrdersResponse>(error);
  }
};

/**
 * Fetches trending products from the API
 * @returns Promise containing array of PublicProduct objects
 * @throws Error if API request fails
 */
export const fetchTrendingProducts = async (): Promise<PublicProduct[]> => {
  try {
    const response = await axios.get<PublicProduct[]>(
      `${API_URL}/public/trending-products`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/*
 * Api call that ask for a order details
 * @param axiosInstance an instance of the user session's token
 * @param orderId the id of the order
 */
export const showOrder = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  orderId: number
): Promise<HttpResponse<IOrderDetails>> => {
  try {
    const { data, status } = await axiosInstance.get<IOrderDetails>(
      `/orders/${orderId}`
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<IOrderDetails>(error);
  }
};

/*
 * Api call that updates the order with the given body
 * @param axiosInstance an instance of the user session's token
 * @param orderId the id of the order
 * @param updateBody is the order update body provided by the user input
 */
export const patchOrder = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  orderId: number,
  updateBody: IUpdateOrder
): Promise<HttpResponse<{ message: string; order: IOrderDetails }>> => {
  try {
    const { data, status } = await axiosInstance.patch<{
      message: string;
      order: IOrderDetails;
    }>(`/orders/${orderId}`, updateBody);
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<{ message: string; order: IOrderDetails }>(
      error
    );
  }
};

export const fetchPublicProductById = async (
  productId: number
): Promise<PublicProductDetails> => {
  try {
    const response = await axios.get<PublicProductDetails>(
      `${API_URL}/public/products/${productId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchPublicProducts = async (
  params: ProductsQueryParams
): Promise<PublicProductListResponse> => {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) queryString.append(key, value.toString());
  });

  const response = await axios.get<PublicProductListResponse>(
    `${API_URL}/public/products?${queryString.toString()}`
  );
  return response.data;
};

export const fetchPublicSizes = async (): Promise<Size[]> => {
  try {
    const response = await axios.get<{ sizes: Size[] }>(
      `${API_URL}/sizes/active`
    );
    return response.data.sizes;
  } catch (error) {
    throw error;
  }
};

/**
 * Obtiene la información del perfil del usuario autenticado
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Instancia de axios autenticada con el token del usuario
 * @returns {Promise<UserProfileResponse>} Datos del perfil del usuario
 * @throws {Error} Error si falla la solicitud
 */
export const fetchUserProfile = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>
): Promise<UserProfileResponse> => {
  try {
    const response = await axiosInstance.get<UserProfileResponse>(
      "/users/profile"
    );
    return response.data;
  } catch (error) {
    console.error("Error en fetchUserProfile:", error);
    throw error;
  }
};

/**
 * Actualiza la información del perfil del usuario, incluyendo su imagen
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Instancia de axios autenticada
 * @param {UpdateProfileRequest} updateData - Datos para actualizar el perfil
 * @returns {Promise<UserProfileResponse>} Datos actualizados del perfil del usuario
 * @throws {Error} Error si falla la solicitud
 */
export const updateUserProfile = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  updateData: UpdateProfileRequest
): Promise<UserProfileResponse> => {
  try {
    const response = await axiosInstance.put<UserProfileResponse>(
      "/users/profile",
      updateData
    );
    return response.data;
  } catch (error) {
    console.error("Error en updateUserProfile:", error);
    throw error;
  }
};

/**
 * Actualiza la contraseña del usuario
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - Instancia de axios autenticada
 * @param {UpdatePasswordRequest} updateData - Datos para actualizar la contraseña
 * @returns {Promise<UpdatePasswordResponse>} Respuesta de la API
 * @throws {Error} Error si falla la solicitud
 */
export const updateUserPassword = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  updateData: UpdatePasswordRequest
): Promise<UpdatePasswordResponse> => {
  try {
    const response = await axiosInstance.put<UpdatePasswordResponse>(
      "/users/update-password",
      updateData
    );
    return response.data;
  } catch (error) {
    console.error("Error en updateUserPassword:", error);
    throw error;
  }
};

/**
 * Creates a new delivery address.
 *
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated Axios instance used for making API requests.
 * @param {Address} addressData - The address data to be created, including user ID, address name, street, number, neighborhood, references, state, city, postal code, and whether it is the default address.
 * @returns {Promise<AddressResponse>} A promise that resolves to the response data from the API, which includes a success message and the created address object.
 * @throws {Error} Throws an error if the API request fails.
 */
export const createDeliveryAddress = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  addressData: Address
): Promise<AddressResponse> => {
  try {
    const response = await axiosInstance.post<AddressResponse>(
      "/delivery-addresses",
      addressData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Updates an existing delivery address.
 *
 * @param {ReturnType<typeof createAuthenticatedAxiosInstance>} axiosInstance - The authenticated Axios instance used for making API requests.
 * @param {number} addressId - The ID of the address to be updated.
 * @param {Partial<Address>} addressData - The updated address data. This can include any subset of the address fields.
 * @returns {Promise<Address>} A promise that resolves to the updated address data returned from the API.
 * @throws {Error} Throws an error if the API request fails.
 */
export const updateDeliveryAddress = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  addressId: number,
  addressData: Partial<Address>
): Promise<Address> => {
  try {
    const response = await axiosInstance.put<Address>(
      `/delivery-addresses/${addressId}`,
      addressData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
/*
 * Api call that get the payment methods
 * @param axiosInstance an instance of the user session's token
 */
export const getPaymentMethods = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>
): Promise<HttpResponse<IPaymentMethod[]>> => {
  try {
    const { data } = await axiosInstance.get<IPaymentMethod[]>(
      `/payment-methods`
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<IPaymentMethod[]>(error);
  }
};

/*
 * Api call that changes the status of payment methods
 * @param axiosInstance an instance of the user session's token
 */
export const changePaymentMethodStatus = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  paymentMethodId: number
): Promise<HttpResponse<{ message: string }>> => {
  try {
    const { data } = await axiosInstance.patch<{ message: string }>(
      `/payment-methods/${paymentMethodId}/toggle-status`
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<{ message: string }>(error);
  }
};

/*
 * Api call that get the bank accounts
 * @param axiosInstance an instance of the user session's token
 */
export const getBankAccounts = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>
): Promise<HttpResponse<IBankAccount[]>> => {
  try {
    const { data } = await axiosInstance.get<IBankAccount[]>(`/bank-accounts`);
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<IBankAccount[]>(error);
  }
};

/*
 * Api call that post a bank account
 * @param axiosInstance an instance of the user session's token
 */
export const postBankAccount = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  body: IPostBankAccount
): Promise<HttpResponse<{ message: string }[]>> => {
  try {
    const { data } = await axiosInstance.post<{ message: string }[]>(
      `/bank-accounts`,
      body
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<{ message: string }[]>(error);
  }
};

/*
 * Api call that get the bank accounts
 * @param axiosInstance an instance of the user session's token
 */
export const setDefaultBankAccount = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  bankAccountId: number
): Promise<HttpResponse<{ message: string }>> => {
  try {
    const { data } = await axiosInstance.patch<{ message: string }>(
      `/bank-accounts/${bankAccountId}/set-default`
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<{ message: string }>(error);
  }
};

/*
 * Api call that get the bank accounts
 * @param axiosInstance an instance of the user session's token
 */
export const changeBankAccountStatus = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  bankAccountId: number
): Promise<HttpResponse<{ message: string }>> => {
  try {
    const { data } = await axiosInstance.patch<{ message: string }>(
      `/bank-accounts/${bankAccountId}/toggle-status`
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<{ message: string }>(error);
  }
};

/**
 * Fetches related products based on categories
 * @param {string} categories - Comma-separated category IDs
 * @returns {Promise<PublicProductListResponse>} A promise that resolves to the related products response
 * @throws {Error} If there's an error fetching the related products
 */
export const fetchRelatedProducts = async (
  categories: string
): Promise<PublicProductListResponse> => {
  try {
    const response = await axios.get<PublicProductListResponse>(
      `${API_URL}/public/products/related`,
      {
        params: {
          categories,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Api call that saves the clicked heart icon on a related product
 * this may favorite or unfavorite a product and add or remove it from the client's wishlist
 */
export const toggleFavorite = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  id: number
): Promise<HttpResponse<IFavoriteProduct[]>> => {
  try {
    const { data } = await axiosInstance.post<IFavoriteProduct[]>(
      `/ordersClients/favorites/${id}`
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<IFavoriteProduct[]>(error);
  }
};

/**
 * Api call that saves the clicked heart icon on a related product
 * this may favorite or unfavorite a product and add or remove it from the client's wishlist
 */
export const getFavorites = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>
): Promise<HttpResponse<IFavoriteProduct[]>> => {
  try {
    const { data } = await axiosInstance.get<IFavoriteProduct[]>(
      `/ordersClients/favorites/`
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<IFavoriteProduct[]>(error);
  }
};

/**
 * Fetches payment methods available for the user
 * @param axiosInstance - The authenticated axios instance
 * @returns Promise with the payment methods response
 */
export const fetchPaymentMethods = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>
): Promise<PaymentMethodsResponse> => {
  try {
    const response = await axiosInstance.get<PaymentMethodsResponse>(
      "/payment-methods/user"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    throw error;
  }
};

/**
 * Api call that gets the client's orders data
 */
export const getClientOrders = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  filters: IClientOrderFilters
): Promise<HttpResponse<IPaginatedClientOrders>> => {
  try {
    const { data } = await axiosInstance.get<IPaginatedClientOrders>(
      "/ordersClients",
      {
        params: filters,
      }
    );
    return HttpResponse.ok(data);
  } catch (error) {
    return catchErrorOnRequest<IPaginatedClientOrders>(error);
  }
};
/* Creates a new order for a client
 * @param axiosInstance - The authenticated axios instance
 * @param orderData - The order data to create
 * @returns Promise with the created order response
 */
export const createOrder = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  orderData: ICreateOrderRequest
): Promise<ICreateOrderResponse> => {
  try {
    const response = await axiosInstance.post<ICreateOrderResponse>(
      "/ordersClients/create",
      orderData,
      {
        timeout: 120000, // 2 minutos de timeout para órdenes
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating order:", error);
    throw error;
  }
};

/**
 * Obtiene los detalles de una orden específica para un cliente
 * @param axiosInstance - La instancia autenticada de axios
 * @param orderId - El ID de la orden a consultar
 * @returns Promise con los detalles de la orden
 */
export const getClientOrderDetails = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  orderId: string | number
): Promise<any> => {
  try {
    const response = await axiosInstance.get(`/ordersClients/${orderId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching client order details:", error);
    throw error;
  }
};
/**
 * Fetches all shipping costs from the API
 * @param axiosInstance - The authenticated axios instance
 * @returns Promise with all shipping costs
 */
export const fetchAllShippingCosts = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>
): Promise<IShippingCost[]> => {
  try {
    const response = await axiosInstance.get<IShippingCost[]>(
      "/admin/shipping-costs"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching shipping costs:", error);
    throw error;
  }
};

/**
 * Fetches public shipping costs from the API (no authentication required)
 * @returns Promise with all public shipping costs
 */
export const fetchPublicShippingCosts = async (): Promise<IShippingCost[]> => {
  try {
    const response = await axios.get<IShippingCost[]>(
      `${API_URL}/public/shipping-costs`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching public shipping costs:", error);
    throw error;
  }
};

/**
 * Fetches a specific shipping cost by ID
 * @param axiosInstance - The authenticated axios instance
 * @param id - The shipping cost ID
 * @returns Promise with the shipping cost details
 */
export const fetchShippingCostById = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  id: number
): Promise<IShippingCost> => {
  try {
    const response = await axiosInstance.get<IShippingCost>(
      `/admin/shipping-costs/${id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching shipping cost:", error);
    throw error;
  }
};

/**
 * Updates a shipping cost
 * @param axiosInstance - The authenticated axios instance
 * @param id - The shipping cost ID
 * @param updateData - The data to update
 * @returns Promise with the update response
 */
export const updateShippingCost = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  id: number,
  updateData: IUpdateShippingCostRequest
): Promise<IUpdateShippingCostResponse> => {
  try {
    const response = await axiosInstance.put<IUpdateShippingCostResponse>(
      `/admin/shipping-costs/${id}`,
      updateData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating shipping cost:", error);
    throw error;
  }
};

/**
 * Updates all shipping costs with the same cost and estimated days
 * @param axiosInstance - The authenticated axios instance
 * @param updateData - The data to update for all shipping costs
 * @returns Promise with the update response
 */
export const updateAllShippingCosts = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  updateData: {
    cost: number;
    estimated_days: number;
  }
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.put<{ message: string }>(
      `/admin/shipping-costs/update-all`,
      updateData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating all shipping costs:", error);
    throw error;
  }
};

/**
 * Sends a contact form message
 * @param contactData - The contact form data
 * @returns Promise with the response from the API
 * @throws Error if the request fails
 */
export const sendContactMessage = async (
  contactData: IContactRequest
): Promise<IContactResponse> => {
  try {
    const response = await axios.post<IContactResponse>(
      `${API_URL}/contact`,
      contactData
    );
    return response.data;
  } catch (error) {
    console.error("Error sending contact message:", error);
    throw error;
  }
};

/**
 * Envía una reseña de producto
 * @param axiosInstance - Instancia autenticada de axios
 * @param review - Objeto con product_id, user_id, comment, qualification
 * @returns Respuesta de la API
 */
export const postReview = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  review: IReviewRequest
): Promise<IReviewResponse> => {
  try {
    const response = await axiosInstance.post<IReviewResponse>('/reviews', review);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Obtiene la calificación del usuario para un producto específico
 * @param axiosInstance - Instancia autenticada de axios
 * @param productId - ID del producto
 * @returns Review del usuario para ese producto (o null si no existe)
 */
export const getMyReview = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  productId: number
): Promise<IReviewResponse | null> => {
  try {
    const response = await axiosInstance.get<IReviewResponse>(`/products/${productId}/my-review`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Obtiene todas las calificaciones de un producto específico
 * @param productId - ID del producto
 * @returns Array de reviews del producto
 */
export const getProductReviews = async (
  productId: number
): Promise<IReviewDetailResponse[]> => {
  try {
    const response = await axios.get<IReviewDetailResponse[]>(`${API_URL}/public/products/${productId}/reviews`);
    return response.data;
  } catch (error) {
    console.error("Error fetching product reviews:", error);
    return [];
  }
};

// ==================== REPORTES ====================

/**
 * Obtiene el reporte de ventas por período
 * @param axiosInstance - Instancia autenticada de axios
 * @param period - Período del reporte (daily, weekly, monthly, yearly)
 * @param startDate - Fecha de inicio (opcional)
 * @param endDate - Fecha de fin (opcional)
 * @returns Reporte de ventas
 */
export const fetchSalesReport = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  period: ReportPeriod,
  startDate?: string,
  endDate?: string
): Promise<ISalesReportResponse> => {
  try {
    const params: any = { period };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await axiosInstance.get<ISalesReportResponse>(
      "/admin/reports/sales",
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Obtiene los productos más vendidos
 * @param axiosInstance - Instancia autenticada de axios
 * @param limit - Número de productos a mostrar (opcional)
 * @param startDate - Fecha de inicio (opcional)
 * @param endDate - Fecha de fin (opcional)
 * @returns Lista de productos más vendidos
 */
export const fetchBestSellingProducts = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  limit?: number,
  startDate?: string,
  endDate?: string
): Promise<IBestSellingProductsResponse> => {
  try {
    const params: any = {};
    if (limit) params.limit = limit;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await axiosInstance.get<IBestSellingProductsResponse>(
      "/admin/reports/best-selling-products",
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Obtiene el reporte de nuevos usuarios
 * @param axiosInstance - Instancia autenticada de axios
 * @param period - Período del reporte (daily, weekly, monthly, yearly)
 * @param startDate - Fecha de inicio (opcional)
 * @param endDate - Fecha de fin (opcional)
 * @returns Reporte de nuevos usuarios
 */
export const fetchNewUsersReport = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  period: ReportPeriod,
  startDate?: string,
  endDate?: string
): Promise<INewUsersReportResponse> => {
  try {
    const params: any = { period };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await axiosInstance.get<INewUsersReportResponse>(
      "/admin/reports/new-users",
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Obtiene los usuarios más frecuentes
 * @param axiosInstance - Instancia autenticada de axios
 * @param limit - Número de usuarios a mostrar (opcional)
 * @param startDate - Fecha de inicio (opcional)
 * @param endDate - Fecha de fin (opcional)
 * @returns Lista de usuarios más frecuentes
 */
export const fetchFrequentUsers = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  limit?: number,
  startDate?: string,
  endDate?: string
): Promise<IFrequentUsersResponse> => {
  try {
    const params: any = {};
    if (limit) params.limit = limit;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await axiosInstance.get<IFrequentUsersResponse>(
      "/admin/reports/frequent-users",
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Obtiene el resumen del dashboard
 * @param axiosInstance - Instancia autenticada de axios
 * @param period - Período del reporte (opcional)
 * @returns Resumen del dashboard
 */
export const fetchDashboardSummary = async (
  axiosInstance: ReturnType<typeof createAuthenticatedAxiosInstance>,
  period?: DashboardPeriod
): Promise<IDashboardSummaryResponse> => {
  try {
    const params: any = {};
    if (period) params.period = period;
    
    const response = await axiosInstance.get<IDashboardSummaryResponse>(
      "/admin/reports/dashboard-summary",
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
